/**
 * The collect mode: the state behind "draw, draw, done".
 *
 * Marking an asset takes a region per relevant area of the drawing — the symbol,
 * the tag balloon, any caption worth reading — so a draw mode that ends at the first
 * mouse-up cannot express it. The mode stays armed instead, and each drag appends a
 * region to a draft the workspace owns, and so does each click on a click target.
 * The canvas reports drags and clicks and renders the draft it is handed; it holds
 * none of this itself.
 *
 * Nothing persists until done: one create call and one identify call fire together
 * at the end, so an abandoned drawing leaves no row behind.
 */
import type { BoundingBox, ClickTarget, IdentifyAssetKind, IdentifyRegion } from '../types';

/** Which element kind a collected draft belongs to. */
export type GeometryTargetKind =
  | 'equipment'
  | 'equipmentInstrument'
  | 'node'
  | 'nodeInstrument'
  | 'controlLoop';

/**
 * An existing element the next drawing belongs to.
 *
 * `replace` is the redraw: the new regions become the element's whole list. `append`
 * adds to what is already there, which is what "Add region" in the instrument editor
 * arms. Absent entirely when the SME is drawing something new.
 */
export interface GeometryTarget {
  kind: GeometryTargetKind;
  id: string;
  op: 'replace' | 'append';
}

/** The equipment or line a new instrument hangs off. */
interface InstrumentContext {
  contextType: 'equipment' | 'node';
  contextId: string;
}

/**
 * One region of a draft: the box the SME drew, the box of a click target the SME
 * clicked, or a saved region the draft was seeded with.
 */
export interface CollectedRegion {
  box: BoundingBox;
  /** The click target this region is the box of. Absent for a drag or a seeded region. */
  clickTarget?: ClickTarget;
}

/** The regions collected so far, and what they are being collected for. */
export interface BoxDraft {
  kind: IdentifyAssetKind;
  /** The element being redrawn or added to. Absent while drawing a new element. */
  target?: GeometryTarget;
  regions: CollectedRegion[];
  /**
   * The parent a new instrument hangs off, resolved when its first region is drawn and
   * kept until Done, so a rail tab change in between does not move it. Null when
   * nothing resolved. Absent on a redraw, which keeps the parent it has.
   */
  context?: InstrumentContext | null;
  /** Started by the kind pill. Its save leaves nothing armed, so the next drag asks again. */
  fromKindPill?: boolean;
}

/**
 * Arms the mode. A new marking starts empty; redrawing an existing one starts from
 * the regions it already has, so Escape pops those too and the SME sees one list
 * rather than a saved set and a separate draft.
 */
export function armDraft(
  kind: IdentifyAssetKind,
  target?: GeometryTarget,
  seedRegions: BoundingBox[] = [],
): BoxDraft {
  return { kind, target, regions: seedRegions.map((box) => ({ box })) };
}

export function appendRegion(draft: BoxDraft, box: BoundingBox): BoxDraft {
  return { ...draft, regions: [...draft.regions, { box }] };
}

/** A click on a click target appends that target's own box, not a box around the pointer. */
export function appendClickTargetRegion(draft: BoxDraft, clickTarget: ClickTarget): BoxDraft {
  return { ...draft, regions: [...draft.regions, { box: clickTarget.bbox, clickTarget }] };
}

/**
 * Backs out the last region. An empty draft has nothing to back out of, so Escape
 * there means "leave the collect mode" and this answers `null`.
 */
export function removeLastRegion(draft: BoxDraft): BoxDraft | null {
  if (draft.regions.length === 0) return null;
  return { ...draft, regions: draft.regions.slice(0, -1) };
}

/**
 * Replaces one region of an element in place, keeping the rest and the singular
 * `bbox` in step. Only the first region reaches `bbox`, so moving a later region
 * leaves the element's anchor exactly where it was.
 */
export function withRegionAt<T extends { bbox: BoundingBox | null; bboxes?: BoundingBox[] }>(
  element: T,
  regionIndex: number,
  box: BoundingBox,
): T {
  const regions = element.bboxes && element.bboxes.length > 0
    ? element.bboxes
    : element.bbox ? [element.bbox] : [];
  const next = regions.length > 0
    ? regions.map((region, index) => (index === regionIndex ? box : region))
    : [box];
  return { ...element, bboxes: next, bbox: next[0] };
}

/** A draft is finishable once it holds a region: identify rejects an empty region list. */
export function canFinish(draft: BoxDraft): boolean {
  return draft.regions.length > 0;
}

/**
 * What the element's region list becomes when the draft is committed: the draft
 * alone for a new element or a redraw, the existing list plus the draft for an
 * append. The existing regions carry no click target: a saved marking keeps boxes only.
 */
export function committedRegions(draft: BoxDraft, existingRegions: BoundingBox[]): CollectedRegion[] {
  if (draft.target?.op === 'append') return [...existingRegions.map((box) => ({ box })), ...draft.regions];
  return draft.regions;
}

/** A committed region as identify reads it: a clicked one by its target's id, any other by its box. */
export function identifyRegion(region: CollectedRegion): IdentifyRegion {
  if (region.clickTarget) return { kind: 'click_target', id: region.clickTarget.id };
  return { kind: 'drawn', bbox: region.box };
}
